import { LanguageCode, BlogPost } from '../types';
import { LANGUAGES, TRANSLATIONS, SEO_KEYWORDS } from '../constants';

const DOMAIN = 'https://yesqrcode.com';

export type SeoMetadata = {
  lang: LanguageCode;
  pathname: string;
  cleanPath: string;
  title: string;
  description: string;
  keywords: string[];
  image: string;
  canonical: string;
  alternates: Array<{ lang: string; href: string }>;
};

export const getCleanPath = (pathname: string): string => {
  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments.length > 0 && LANGUAGES[pathSegments[0] as LanguageCode]) {
    pathSegments.shift();
  }
  return '/' + pathSegments.join('/');
};

type BuildSeoArgs = {
  lang: LanguageCode;
  pathname: string;
  slug?: string;
  posts?: BlogPost[];
};

type KeywordCategory = keyof (typeof SEO_KEYWORDS)['en'];

export const buildSeoMetadata = ({
  lang,
  pathname,
  slug,
  posts = [],
}: BuildSeoArgs): SeoMetadata => {
  const enTranslation = TRANSLATIONS.en;
  const t = TRANSLATIONS[lang] ?? enTranslation;
  const rawCleanPath = getCleanPath(pathname);
  const aliasMap: Record<string, string> = {
    '/generate': '/qrcode-generator',
    '/scan': '/qrcode-scanner',
  };
  const normalizedPath =
    aliasMap[rawCleanPath] ?? (rawCleanPath === '/' ? '/qrcode-generator' : rawCleanPath);

  const getKeywords = (targetLang: LanguageCode, key: KeywordCategory) =>
    SEO_KEYWORDS[targetLang]?.[key] ?? SEO_KEYWORDS.en[key];

  const meta: SeoMetadata = {
    lang,
    pathname,
    cleanPath: normalizedPath,
    title: `${t.hero?.title ?? enTranslation.hero.title} - YesQRCode`,
    description: t.hero?.desc ?? enTranslation.hero.desc,
    keywords: getKeywords(lang, 'default'),
    image: 'https://yesqrcode.com/og-image.png',
    canonical: `${DOMAIN}/${lang}${normalizedPath === '/' ? '' : normalizedPath}`,
    alternates: Object.keys(LANGUAGES).map((code) => ({
      lang: code,
      href: `${DOMAIN}/${code}${normalizedPath === '/' ? '' : normalizedPath}`,
    })),
  };

  if (normalizedPath.includes('/qrcode-generator')) {
    meta.title = t.pageMeta?.generator?.title ?? enTranslation.pageMeta.generator.title;
    meta.description =
      t.pageMeta?.generator?.description ?? enTranslation.pageMeta.generator.description;
    meta.keywords = t.pageMeta?.generator?.keywords ?? enTranslation.pageMeta.generator.keywords;
  } else if (normalizedPath.includes('/text-to-qrcode')) {
    meta.title = t.pageMeta?.textToQr?.title ?? enTranslation.pageMeta.textToQr.title;
    meta.description =
      t.pageMeta?.textToQr?.description ?? enTranslation.pageMeta.textToQr.description;
    meta.keywords = t.pageMeta?.textToQr?.keywords ?? enTranslation.pageMeta.textToQr.keywords;
  } else if (normalizedPath.includes('/link-to-qrcode')) {
    meta.title = t.pageMeta?.linkToQr?.title ?? enTranslation.pageMeta.linkToQr.title;
    meta.description =
      t.pageMeta?.linkToQr?.description ?? enTranslation.pageMeta.linkToQr.description;
    meta.keywords = t.pageMeta?.linkToQr?.keywords ?? enTranslation.pageMeta.linkToQr.keywords;
  } else if (normalizedPath.includes('/qrcode-scanner')) {
    meta.title = t.pageMeta?.scanner?.title ?? enTranslation.pageMeta.scanner.title;
    meta.description =
      t.pageMeta?.scanner?.description ?? enTranslation.pageMeta.scanner.description;
    meta.keywords = t.pageMeta?.scanner?.keywords ?? enTranslation.pageMeta.scanner.keywords;
  } else if (normalizedPath.includes('/blog')) {
    if (slug) {
      const post = posts.find((p) => p.slug === slug);
      if (post) {
        meta.title = `${post.title} - YesQRCode Blog`;
        meta.description = post.description || post.excerpt;
        const derivedKeywords = post.keywords && post.keywords.length > 0 ? post.keywords : post.tags;
        if (derivedKeywords && derivedKeywords.length > 0) {
          meta.keywords = derivedKeywords;
        }
        if (post.image) {
          meta.image = post.image;
        }
      } else {
        meta.title = `${t.blog?.notFound ?? enTranslation.blog.notFound} - YesQRCode`;
      }
    } else {
      meta.title = `${t.nav?.blog ?? enTranslation.nav.blog} - YesQRCode`;
      meta.description = t.blog?.subtitle ?? enTranslation.blog.subtitle;
      meta.keywords = getKeywords(lang, 'blog');
    }
  } else if (normalizedPath.includes('/help')) {
    meta.title = `${t.info?.helpTitle ?? enTranslation.info.helpTitle} - YesQRCode`;
    meta.description = t.info?.helpSubtitle ?? enTranslation.info.helpSubtitle;
  } else if (normalizedPath.includes('/contact')) {
    meta.title = `${t.info?.contactTitle ?? enTranslation.info.contactTitle} - YesQRCode`;
    meta.description = t.info?.contactSubtitle ?? enTranslation.info.contactSubtitle;
  } else if (normalizedPath.includes('/privacy')) {
    meta.title = `${t.info?.privacyTitle ?? enTranslation.info.privacyTitle} - YesQRCode`;
  } else if (normalizedPath.includes('/terms')) {
    meta.title = `${t.info?.termsTitle ?? enTranslation.info.termsTitle} - YesQRCode`;
  }

  return meta;
};
